import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import {
  analyzeSowBatchUpload,
  fetchHistoricalSowHistory,
  fetchSowHistoryDetail,
} from "../api/sowApi";
import "../styles/rules.css";

const contractTypes = ["T&M", "Fixed Bid", "Custom"];

// Helper to get user email from localStorage
function getUserEmail() {
  const direct = (
    localStorage.getItem("user_email") ||
    localStorage.getItem("email") ||
    ""
  ).trim();
  if (direct) return direct.toLowerCase();
  try {
    const u = JSON.parse(
      localStorage.getItem("user") ||
      localStorage.getItem("currentUser") ||
      "null"
    );
    if (u?.email) return String(u.email).trim().toLowerCase();
  } catch {}
  return "";
}

function safeParseRaw(raw) {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function clampPct(n) {
  if (typeof n !== "number" || Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

export default function SowTestAnalysisPage() {
  // --- user email for visibility ---
  const userEmail = useMemo(() => getUserEmail(), []);

  // --- form state ---
  const [accountName, setAccountName] = useState("");
  const [sfOpportunityId, setSfOpportunityId] = useState("");
  const [contractType, setContractType] = useState("T&M");
  const [stage, setStage] = useState("");
  const [sowDrafter, setSowDrafter] = useState("");
  const [sowReviewer, setSowReviewer] = useState("");
  const [slGdEmail, setSlGdEmail] = useState("");
  const [files, setFiles] = useState([]);

  // --- history + detail ---
  const [history, setHistory] = useState([]);
  const [historySearch, setHistorySearch] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // --- ui flags ---
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState("");

  // --- dialog for analysis results ---
  const [showResultDialog, setShowResultDialog] = useState(false);

  // ----------------------------------------------------
  // History load
  // ----------------------------------------------------
  const loadHistory = async ({ keepSelection = true } = {}) => {
    try {
      setError("");
      const data = await fetchHistoricalSowHistory(200, userEmail);
      const list = data || [];
      setHistory(list);

      if (!keepSelection || !selectedId) {
        if (list.length > 0) {
          await selectRow(list[0].id, list[0], { silent: true });
        } else {
          setSelectedId(null);
          setSelectedDetail(null);
        }
      } else {
        // selection exists: refresh detail (optional)
        const stillExists = list.some((r) => r.id === selectedId);
        if (!stillExists) {
          if (list.length > 0) await selectRow(list[0].id, list[0], { silent: true });
          else {
            setSelectedId(null);
            setSelectedDetail(null);
          }
        }
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load historical SOW analysis.");
    }
  };

  useEffect(() => {
    loadHistory({ keepSelection: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------
  // Search (front-end filter)
  // ----------------------------------------------------
  const filteredHistory = useMemo(() => {
    const q = (historySearch || "").trim().toLowerCase();
    if (!q) return history;

    return (history || []).filter((h) => {
      const hay = [
        h.id,
        h.sf_opportunity_id,
        h.opportunity_name,
        h.account_name,
        h.contract_type,
        h.source,
        h.stage,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [history, historySearch]);

  // ----------------------------------------------------
  // File input
  // ----------------------------------------------------
  const onFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  // ----------------------------------------------------
  // Select a row -> ALWAYS fetch detail
  // ----------------------------------------------------
  const selectRow = async (id, baseRow, opts = {}) => {
    setSelectedId(id);
    setDetailLoading(true);

    // show base row quickly while detail loads
    setSelectedDetail(baseRow || null);

    try {
      const detail = await fetchSowHistoryDetail(id, userEmail);
      setSelectedDetail(detail);
    } catch (err) {
      console.error(err);
      if (!opts?.silent) setError("Failed to load analysis detail.");
      // keep base row visible
      setSelectedDetail(baseRow || null);
    } finally {
      setDetailLoading(false);
    }
  };

  // ----------------------------------------------------
  // Analyze upload
  // ----------------------------------------------------
  const handleAnalyze = async (e) => {
    e.preventDefault();
    setError("");

    if (!accountName.trim()) return setError("Account Name is required.");
    if (!files.length) return setError("Please select at least one SOW file.");

    setBusy(true);
    setProgress(0);
    setStatusText("Preparing upload…");

    const payload = {
      account_name: accountName,
      sf_opportunity_id: sfOpportunityId,
      contract_type: contractType,
      stage,
      sow_drafter: sowDrafter,
      sow_reviewer: sowReviewer,
      sl_gd_email: slGdEmail,
      created_by: userEmail,
      files,
    };

    try {
      const result = await analyzeSowBatchUpload(payload, (pct, phase) => {
        setProgress(clampPct(pct));
        if (phase) setStatusText(phase);
      });

      setProgress(100);
      setStatusText("Analysis complete.");

      await loadHistory({ keepSelection: true });

      const successResults = (Array.isArray(result) ? result : []).filter((r) => r?.id != null);
      if (successResults.length > 0) {
        const last = successResults[successResults.length - 1];
        await selectRow(last.id, last);
        setShowResultDialog(true);
      } else if (Array.isArray(result) && result.some((r) => r?.status === "error")) {
        const firstError = result.find((r) => r?.status === "error");
        setError(`Analysis failed: ${firstError?.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error(err);
      setError("Analysis failed. Please check backend logs.");
      setStatusText("Error while analyzing.");
      setProgress(0);
    } finally {
      setBusy(false);
    }
  };

  // ----------------------------------------------------
  // Downloads
  // ----------------------------------------------------
  const downloadExcel = async (id) => {
    if (!id) return;
    try {
      // ✅ relative path; api baseURL already contains /api
      const resp = await api.get(`/sow-tests/report-excel/${id}`, {
        responseType: "blob",
        timeout: 300000,
      });

      const blob = new Blob([resp.data], {
        type:
          resp.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = `sow_analysis_${id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError("Failed to download Excel report.");
    }
  };

  const downloadSowFile = async (id) => {
    if (!id) return;
    try {
      const resp = await api.get(`/sow-tests/files/${id}`, {
        responseType: "blob",
        timeout: 300000,
      });

      const cd = resp.headers["content-disposition"] || "";
      let filename = "sow_document";
      const match = /filename="?([^"]+)"?/i.exec(cd);
      if (match?.[1]) filename = match[1];

      const blob = new Blob([resp.data]);
      const link = document.createElement("a");
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError("Failed to download SOW file.");
    }
  };

  // ----------------------------------------------------
  // UI blocks
  // ----------------------------------------------------
  const renderProgressBar = () => {
    if (!busy && progress === 0) return null;
    const pct = Math.round(clampPct(progress));

    return (
      <div style={{ marginTop: 8, marginBottom: 8 }}>
        <div
          style={{
            position: "relative",
            width: "100%",
            height: 8,
            borderRadius: 999,
            background: "#e5e7eb",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${Math.min(pct, 100)}%`,
              borderRadius: 999,
              background: "linear-gradient(90deg, #2563eb, #4f46e5, #10b981)",
              transition: "width 0.2s ease-out",
            }}
          />
        </div>
        <div
          style={{
            fontSize: 12,
            marginTop: 4,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>{statusText}</span>
          <span>{pct}%</span>
        </div>
      </div>
    );
  };

  const renderRuleDetails = (raw) => {
    const ruleEvals = raw.rule_evaluations || [];
    if (!ruleEvals.length) {
      return (
        <p style={{ fontSize: 12, color: "#6b7280", marginTop: 12 }}>
          No rule-by-rule details returned. (LLM likely did not return `rule_evaluations`.)
        </p>
      );
    }

    const missingRows = ruleEvals.filter(
      (r) => (r.status || "").toLowerCase() === "missing"
    );

    const missingMandatory = raw.missing_mandatory_rules || [];

    return (
      <div style={{ marginTop: 18 }}>
        {/* Missing sections */}
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
            Missing Sections (Rules with status = missing)
          </h3>
          <div className="rules-table-wrapper">
            <table className="rules-table">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Severity</th>
                  <th>Mandatory</th>
                  <th>Suggestion</th>
                </tr>
              </thead>
              <tbody>
                {missingRows.map((r, idx) => (
                  <tr key={idx}>
                    <td>{r.rule_name || r.name}</td>
                    <td style={{ textTransform: "capitalize" }}>
                      {r.severity || "-"}
                    </td>
                    <td>{r.mandatory ? "Yes" : "No"}</td>
                    <td style={{ whiteSpace: "normal" }}>
                      {r.suggestion || r.comments || "-"}
                    </td>
                  </tr>
                ))}
                {missingRows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="empty-cell">
                      No missing rules detected.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {missingMandatory.length > 0 && (
            <p style={{ fontSize: 12, marginTop: 6 }}>
              <strong>Missing mandatory RuleKeys:</strong>{" "}
              {missingMandatory.join(", ")}
            </p>
          )}
        </div>

        {/* Full rule evaluations */}
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
          Rule-by-Rule Evaluation (Status + Score + Suggestion)
        </h3>
        <div className="rules-table-wrapper">
          <table className="rules-table">
            <thead>
              <tr>
                <th>Rule</th>
                <th>Dimension</th>
                <th>Severity</th>
                <th>Mandatory</th>
                <th>Status</th>
                <th>Score</th>
                <th>Why</th>
                <th>Suggestion</th>
              </tr>
            </thead>
            <tbody>
              {ruleEvals.map((r, idx) => (
                <tr key={idx}>
                  <td>{r.rule_name || r.name}</td>
                  <td>{r.dimension || "-"}</td>
                  <td style={{ textTransform: "capitalize" }}>
                    {r.severity || "-"}
                  </td>
                  <td>{r.mandatory ? "Yes" : "No"}</td>
                  <td>{r.status || "-"}</td>
                  <td>{r.rule_score ?? "-"}</td>
                  <td style={{ whiteSpace: "normal" }}>{r.comments || "-"}</td>
                  <td style={{ whiteSpace: "normal" }}>
                    {r.suggestion || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDetailCard = () => {
    if (!selectedId) {
      return (
        <div className="rules-form">
          <h2>Analysis Detail</h2>
          <p style={{ fontSize: 13, color: "#6b7280" }}>
            Select a record from the left to view details.
          </p>
        </div>
      );
    }

    const sel = selectedDetail || history.find((h) => h.id === selectedId);
    if (!sel) {
      return (
        <div className="rules-form">
          <h2>Analysis Detail</h2>
          <p style={{ fontSize: 13, color: "#6b7280" }}>
            Record not found in history.
          </p>
        </div>
      );
    }

    const raw = safeParseRaw(sel.raw_json);

    const finalOverall =
      raw.final_overall_score_used ?? sel.score_overall ?? 0;

    const riskScore = raw.risk_score ?? sel.score_risk ?? "-";
    const complianceScore = raw.compliance_score ?? sel.score_compliance ?? "-";
    const commercialScore = raw.commercial_score ?? sel.score_commercial ?? "-";

    const overallRationale = raw.overall_rationale || "-";
    const riskRationale = raw.risk_rationale || "-";
    const complianceRationale = raw.compliance_rationale || "-";
    const commercialRationale = raw.commercial_rationale || "-";

    const issues = raw.issues || [];
    const recs = raw.recommendations || [];

    const hasFile = Boolean(sel.file_path);

    return (
      <div className="rules-form">
        <h2 style={{ display: "flex", alignItems: "center", gap: 10 }}>
          Analysis Detail
          {detailLoading && (
            <span style={{ fontSize: 12, color: "#6b7280" }}>Loading…</span>
          )}
        </h2>

        {/* Header + download buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 10,
            gap: 12,
          }}
        >
          <div>
            <p style={{ fontSize: 13, marginBottom: 6 }}>
              <strong>SOW / File:</strong> {sel.opportunity_name || "-"}
            </p>
            <p style={{ fontSize: 13, marginBottom: 6 }}>
              <strong>Account:</strong> {sel.account_name || "-"}
            </p>
            <p style={{ fontSize: 13, marginBottom: 6 }}>
              <strong>Contract Type:</strong> {sel.contract_type || "-"}
            </p>
            <p style={{ fontSize: 13, marginBottom: 6 }}>
              <strong>Source:</strong> {sel.source || "-"}
            </p>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="secondary"
              onClick={() => downloadExcel(sel.id)}
            >
              Download Excel
            </button>

            <button
              type="button"
              className="secondary"
              onClick={() => downloadSowFile(sel.id)}
              disabled={!hasFile}
              title={!hasFile ? "No file stored for this record" : "Download SOW"}
            >
              Download SOW
            </button>

            {hasFile && (
              <a
                href={`/api/sow-tests/files/${sel.id}`}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 12, alignSelf: "center" }}
              >
                Open SOW
              </a>
            )}
          </div>
        </div>

        {/* Score overview */}
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
          Score Overview & Rationale
        </h3>

        <div className="rules-table-wrapper">
          <table className="rules-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Score</th>
                <th>Explanation</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Overall (Final)</td>
                <td>{finalOverall}</td>
                <td style={{ whiteSpace: "normal" }}>{overallRationale}</td>
              </tr>
              <tr>
                <td>Risk</td>
                <td>{riskScore}</td>
                <td style={{ whiteSpace: "normal" }}>{riskRationale}</td>
              </tr>
              <tr>
                <td>Compliance</td>
                <td>{complianceScore}</td>
                <td style={{ whiteSpace: "normal" }}>{complianceRationale}</td>
              </tr>
              <tr>
                <td>Commercial</td>
                <td>{commercialScore}</td>
                <td style={{ whiteSpace: "normal" }}>{commercialRationale}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Computation explanation */}
        {(raw.computed_overall_score_from_rules !== undefined ||
          raw.model_overall_score !== undefined) && (
          <div style={{ marginTop: 10, fontSize: 12, color: "#374151" }}>
            <strong>How “Overall” is computed:</strong>{" "}
            Overall is taken from rules-weighted scoring when available.
            <div style={{ marginTop: 6 }}>
              <div>
                Rules-based overall:{" "}
                <strong>{raw.computed_overall_score_from_rules ?? "-"}</strong>
              </div>
              <div>
                Model overall: <strong>{raw.model_overall_score ?? "-"}</strong>
              </div>
              <div>
                Final used:{" "}
                <strong>{raw.final_overall_score_used ?? finalOverall}</strong>
              </div>
            </div>
          </div>
        )}

        {/* Summary */}
        <p style={{ fontSize: 13, marginTop: 12 }}>
          <strong>Summary:</strong> {raw.summary || sel.summary || "-"}
        </p>

        {/* Issues */}
        <div style={{ marginTop: 12 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
            Key Issues
          </h3>
          <div className="rules-table-wrapper">
            <table className="rules-table">
              <thead>
                <tr>
                  <th>Dimension</th>
                  <th>Severity</th>
                  <th>Description</th>
                  <th>Rule</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((iss, idx) => (
                  <tr key={idx}>
                    <td>{iss.dimension || "-"}</td>
                    <td style={{ textTransform: "capitalize" }}>
                      {iss.severity || "-"}
                    </td>
                    <td style={{ whiteSpace: "normal" }}>
                      {iss.description || "-"}
                    </td>
                    <td>{iss.rule_name || "-"}</td>
                  </tr>
                ))}
                {issues.length === 0 && (
                  <tr>
                    <td colSpan={4} className="empty-cell">
                      No issues returned.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recommendations */}
        <div style={{ marginTop: 12 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
            Recommendations
          </h3>
          {recs.length > 0 ? (
            <ul style={{ paddingLeft: 18, fontSize: 12 }}>
              {recs.map((r, idx) => (
                <li key={idx}>{r}</li>
              ))}
            </ul>
          ) : (
            <p style={{ fontSize: 12, color: "#6b7280" }}>No recommendations returned.</p>
          )}
        </div>

        {/* Rules tables */}
        {renderRuleDetails(raw)}
      </div>
    );
  };

  const renderResultDialog = () => {
    if (!showResultDialog || !selectedDetail) return null;

    const raw = safeParseRaw(selectedDetail.raw_json);
    const finalOverall =
      raw.final_overall_score_used ?? selectedDetail.score_overall ?? 0;
    const riskScore = raw.risk_score ?? selectedDetail.score_risk ?? "-";
    const complianceScore =
      raw.compliance_score ?? selectedDetail.score_compliance ?? "-";
    const commercialScore =
      raw.commercial_score ?? selectedDetail.score_commercial ?? "-";

    const ruleEvals = raw.rule_evaluations || [];
    const missingRows = ruleEvals.filter(
      (r) => (r.status || "").toLowerCase() === "missing"
    );
    const coveredRows = ruleEvals.filter(
      (r) => (r.status || "").toLowerCase() === "covered"
    );
    const partialRows = ruleEvals.filter(
      (r) => (r.status || "").toLowerCase() === "partially_covered"
    );

    return (
      <div
        className="analysis-dialog-overlay"
        onClick={() => setShowResultDialog(false)}
      >
        <div
          className="analysis-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="analysis-dialog-close"
            onClick={() => setShowResultDialog(false)}
            title="Close"
          >
            ✕
          </button>

          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
            Analysis Results
          </h2>
          <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
            {selectedDetail.opportunity_name || "SOW"} —{" "}
            {selectedDetail.account_name || ""} —{" "}
            {selectedDetail.contract_type || ""}
          </p>

          {/* Score cards */}
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 18,
            }}
          >
            {[
              { label: "Overall", value: finalOverall },
              { label: "Risk", value: riskScore },
              { label: "Compliance", value: complianceScore },
              { label: "Commercial", value: commercialScore },
            ].map((s) => {
              const num = typeof s.value === "number" ? s.value : 0;
              const color =
                num >= 80
                  ? "#10b981"
                  : num >= 70
                  ? "#3b82f6"
                  : num >= 50
                  ? "#f59e0b"
                  : "#ef4444";
              return (
                <div
                  key={s.label}
                  style={{
                    flex: "1 1 140px",
                    background: "#f9fafb",
                    borderRadius: 12,
                    padding: "12px 16px",
                    textAlign: "center",
                    border: `2px solid ${color}20`,
                  }}
                >
                  <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
                    {s.label}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color }}>
                    {s.value}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div
            style={{
              background: "#eff6ff",
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 12,
              marginBottom: 16,
              lineHeight: 1.6,
            }}
          >
            {raw.summary || selectedDetail.summary || "No summary available."}
          </div>

          {/* Coverage summary */}
          <div style={{ fontSize: 12, marginBottom: 16 }}>
            <strong>Coverage:</strong> {coveredRows.length} covered,{" "}
            {partialRows.length} partially covered, {missingRows.length} missing
            out of {ruleEvals.length} rules
          </div>

          {/* Missing sections */}
          {missingRows.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Missing Sections ({missingRows.length})
              </h3>
              <div className="rules-table-wrapper">
                <table className="rules-table" style={{ minWidth: 600 }}>
                  <thead>
                    <tr>
                      <th>Rule</th>
                      <th>Severity</th>
                      <th>Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missingRows.map((r, idx) => (
                      <tr key={idx}>
                        <td>{r.rule_name || r.name}</td>
                        <td style={{ textTransform: "capitalize" }}>
                          {r.severity || "-"}
                        </td>
                        <td style={{ whiteSpace: "normal" }}>
                          {r.comments || r.suggestion || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* All rule evaluations */}
          <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            All Rule Evaluations
          </h3>
          <div className="rules-table-wrapper">
            <table className="rules-table" style={{ minWidth: 700 }}>
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Status</th>
                  <th>Score</th>
                  <th>Comment</th>
                </tr>
              </thead>
              <tbody>
                {ruleEvals.map((r, idx) => (
                  <tr key={idx}>
                    <td>{r.rule_name || r.name}</td>
                    <td>
                      <span
                        className={`severity severity-${
                          r.status === "covered"
                            ? "low"
                            : r.status === "partially_covered"
                            ? "medium"
                            : "critical"
                        }`}
                      >
                        {r.status || "-"}
                      </span>
                    </td>
                    <td>{r.rule_score ?? "-"}</td>
                    <td style={{ whiteSpace: "normal" }}>
                      {r.comments || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ textAlign: "right", marginTop: 16 }}>
            <button
              type="button"
              onClick={() => setShowResultDialog(false)}
              style={{
                padding: "8px 20px",
                borderRadius: 999,
                background: "#2563eb",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="rules-page">
      <header className="rules-header">
        <div>
          <h1>Historical SOW Test Analysis</h1>
          <p>
            Upload legacy SOWs, analyze with Rules Engine + Llama + RAG, and
            keep a detailed record (risk/compliance/commercial + missing
            sections + suggestions).
          </p>
        </div>
      </header>

      {renderResultDialog()}

      <div className="rules-layout analysis-layout">
        {/* LEFT */}
        <section className="rules-list">
          <div className="rules-list-header">
            <h2>Upload Old SOWs</h2>
          </div>

          {error && <div className="rules-error">{error}</div>}

          <form onSubmit={handleAnalyze}>
            <div className="form-row two-cols">
              <label>
                Account Name
                <input
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  required
                />
              </label>
              <label>
                Opty ID (optional)
                <input
                  value={sfOpportunityId}
                  onChange={(e) => setSfOpportunityId(e.target.value)}
                  placeholder="e.g. 006xxxxxxxxxxxxx"
                />
              </label>
            </div>

            <div className="form-row two-cols">
              <label>
                Contract Type
                <select
                  value={contractType}
                  onChange={(e) => setContractType(e.target.value)}
                >
                  {contractTypes.map((ct) => (
                    <option key={ct} value={ct}>
                      {ct}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Stage (optional)
                <input
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                  placeholder="e.g. Closed Won / Closed Lost"
                />
              </label>
            </div>

            <div className="form-row two-cols">
              <label>
                SOW Drafter (optional)
                <input
                  value={sowDrafter}
                  onChange={(e) => setSowDrafter(e.target.value)}
                />
              </label>
              <label>
                SOW Reviewer (optional)
                <input
                  value={sowReviewer}
                  onChange={(e) => setSowReviewer(e.target.value)}
                />
              </label>
            </div>

            <div className="form-row">
              <label>
                SL / GD Email (optional)
                <input
                  value={slGdEmail}
                  onChange={(e) => setSlGdEmail(e.target.value)}
                />
              </label>
            </div>

            <div className="form-row">
              <label>
                SOW Files (PDF / DOCX) – multiple
                <input
                  type="file"
                  multiple
                  onChange={onFileChange}
                  accept=".pdf,.doc,.docx"
                />
              </label>
            </div>

            {renderProgressBar()}

            <div className="form-actions">
              <button type="submit" disabled={busy}>
                {busy ? "Analyzing..." : "Analyze SOWs"}
              </button>
            </div>
          </form>

          <hr style={{ margin: "20px 0", borderColor: "#e5e7eb" }} />

          {/* History header + search */}
          <div className="rules-list-header" style={{ gap: 10 }}>
            <h2 style={{ margin: 0 }}>Historical Analysis</h2>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder="Search by ID / file / account / type / source…"
                style={{
                  width: 320,
                  padding: "8px 10px",
                  borderRadius: 12,
                  border: "1px solid #d1d5db",
                  fontSize: 12,
                }}
              />
              {historySearch && (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setHistorySearch("")}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
            Showing <strong>{filteredHistory.length}</strong> of{" "}
            <strong>{history.length}</strong>
          </div>

          <div className="rules-table-wrapper">
            <table className="rules-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Opty ID</th>
                  <th>File</th>
                  <th>Account</th>
                  <th>Type</th>
                  <th>Score</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((h) => (
                  <tr
                    key={h.id}
                    onClick={async () => {
                      await selectRow(h.id, h);
                      setShowResultDialog(true);
                    }}
                    style={{
                      cursor: "pointer",
                      background: selectedId === h.id ? "#eff6ff" : "transparent",
                    }}
                  >
                    <td>{h.id}</td>
                    <td title={h.sf_opportunity_id || ""}>{h.sf_opportunity_id || "-"}</td>
                    <td title={h.opportunity_name || ""}>{h.opportunity_name}</td>
                    <td title={h.account_name || ""}>{h.account_name}</td>
                    <td>{h.contract_type}</td>
                    <td>{h.score_overall}</td>
                    <td>{h.source}</td>
                  </tr>
                ))}
                {filteredHistory.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty-cell">
                      No matches found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* RIGHT */}
        {renderDetailCard()}
      </div>
    </div>
  );
}
